import { AnnotationMetadata } from '@/features/annotation'
import { PdfRect, PdfViewportLike } from '@/features/annotation/types.ts'
import { PageRuntime } from '@/features/annotation/utils/PdfTextIndexer.ts'

import * as pdfCoordinateUtils from './pdfCoordinateUtils'

type Style = { color: string; alpha: number }

interface PdfEngine {
  pageRuntimes: Map<number, PageRuntime>
  annotationRegistry?: Map<string, AnnotationMetadata>
  apply?: (metadata: AnnotationMetadata, commentId: string) => void
}

export class PdfOverlayRenderer {
  static clearCanvas(canvas: HTMLCanvasElement | null): void {
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  /** Do NOT clear controlCanvas; it is input-only. */
  static clearInteractiveOverlays(rt: PageRuntime): void {
    this.clearCanvas(rt.highlightCanvas)
    this.clearCanvas(rt.controlCanvas)
    if (rt.bubbleLayerEl) {
      // remove any portal-rendered children
      rt.bubbleLayerEl.innerHTML = ''
      // optional: hide completely in read-only mode
      rt.bubbleLayerEl.style.display = 'none'
    }
  }

  /** HiDPI-safe resize; draw in CSS pixels. */
  static resizeCanvasToViewport(
    canvas: HTMLCanvasElement,
    viewport: PdfViewportLike
  ): void {
    const dpr = window.devicePixelRatio || 1
    const cssW = Math.floor(viewport.width)
    const cssH = Math.floor(viewport.height)

    canvas.style.width = `${cssW}px`
    canvas.style.height = `${cssH}px`
    canvas.width = Math.floor(cssW * dpr)
    canvas.height = Math.floor(cssH * dpr)

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  /** Draw helper: draw PDF rects onto a target canvas without clearing. */
  private static drawRects(
    canvas: HTMLCanvasElement,
    viewport: PdfViewportLike,
    rects: PdfRect[],
    style: Style
  ): void {
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.save()
    ctx.globalAlpha = style.alpha
    ctx.fillStyle = style.color

    for (const r of rects) {
      const rectPdf = {
        x: r.leftOffset,
        y: r.topOffset,
        w: r.width,
        h: r.height,
      }
      const vp = pdfCoordinateUtils.rectToViewportRect(viewport, rectPdf)
      ctx.fillRect(vp.x, vp.y, vp.w, vp.h)
    }

    ctx.restore()
  }

  static updateCursorStyle(
    canvas: HTMLCanvasElement | null,
    style: string
  ): void {
    if (!canvas) return
    canvas.style.cursor = style
  }

  static clearHighlightCanvasesAcrossPages<
    T extends { highlightCanvas: HTMLCanvasElement | null },
  >(pageRuntimes: Map<number, T>): void {
    for (const runtime of pageRuntimes.values()) {
      this.clearCanvas(runtime.highlightCanvas)
    }
  }

  static drawHighlightRectsOnPage(
    canvas: HTMLCanvasElement | null,
    viewport: PdfViewportLike | null,
    rects: PdfRect[],
    style: Style
  ): void {
    if (!canvas || !viewport) return
    this.drawRects(canvas, viewport, rects, style)
  }

  /**
   * Create a debounced function for redrawing overlays on viewport/canvas resize.
   *
   * Debounce prevents excessive canvas redraws during rapid viewport changes
   * (e.g., window resize, zoom). 1s debounce balances responsiveness with
   * performance on large PDFs.
   *
   * NOTE: PDF canvas itself is NOT redrawn (future optimization); only overlay
   * canvases are redrawn.
   *
   * @param engine - PdfAnnotationEngine instance with pageRuntimes and registry
   * @param debounceMs - Debounce delay in milliseconds (default: 1000ms)
   * @returns Debounced redraw function
   */
  static createDebouncedRedrawOverlays(
    engine: PdfEngine,
    debounceMs: number = 1000
  ): () => void {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const scheduleRedraw = () => {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(runRedraw, debounceMs)
    }

    const runRedraw = () => {
      if (!engine.pageRuntimes) return

      engine.pageRuntimes.forEach((runtime) => {
        this.redrawRuntimeOverlays(runtime)
      })

      this.reapplyAllAnnotations(engine)
    }

    return scheduleRedraw
  }

  private static redrawRuntimeOverlays(runtime: PageRuntime): void {
    const viewport = runtime.viewport
    if (!viewport) return

    this.resizeOverlayCanvases(runtime, viewport)
    this.resizeBubbleLayer(runtime, viewport)
    this.recomputeWordViewportRects(runtime, viewport)
  }

  private static resizeOverlayCanvases(
    runtime: PageRuntime,
    viewport: PdfViewportLike
  ): void {
    if (runtime.highlightCanvas) {
      this.resizeCanvasToViewport(runtime.highlightCanvas, viewport)
    }
    if (runtime.controlCanvas) {
      this.resizeCanvasToViewport(runtime.controlCanvas, viewport)
    }
  }

  private static resizeBubbleLayer(
    runtime: PageRuntime,
    viewport: PdfViewportLike
  ): void {
    const el = runtime.bubbleLayerEl
    if (!el) return

    const cssW = Math.floor(viewport.width)
    const cssH = Math.floor(viewport.height)
    el.style.width = `${cssW}px`
    el.style.height = `${cssH}px`
  }

  private static recomputeWordViewportRects(
    runtime: PageRuntime,
    viewport: PdfViewportLike
  ): void {
    const words = runtime.words
    if (!Array.isArray(words) || words.length === 0) return

    for (const word of words) {
      if (!word.rectPdf) continue
      word.rectVp = pdfCoordinateUtils.rectToViewportRect(
        viewport,
        word.rectPdf
      )
    }
  }

  private static reapplyAllAnnotations(engine: PdfEngine): void {
    if (!engine.annotationRegistry || typeof engine.apply !== 'function') return

    engine.annotationRegistry.forEach(
      (metadata: AnnotationMetadata, commentId: string) => {
        engine.apply!(metadata, commentId)
      }
    )
  }
}
